import { useState, useEffect } from 'react';
import axios from 'axios';

// Use relative URLs in production, localhost in development
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:8081";

interface ICP {
  filename: string;
  profile_name: string;
  profile_type: string;
  eligibility_status: string;
}

interface TestSession {
  id: string;
  agent_name: string;
  agent_version: number;
  icp_profile_name: string;
  icp_profile_type: string;
  icp_eligibility_status: string;
  started_at: string;
  completed_at: string;
  status: string;
  total_turns: number;
  appointment_booked: boolean;
  test_duration_seconds: number;
  batch_number?: string;
}

interface Message {
  id: string;
  turn_number: number;
  role: string;
  message: string;
  timestamp: string;
  response_time_ms: number;
}

interface Issue {
  id: string;
  turn_number: number;
  issue_type: string;
  issue_description: string;
  created_at: string;
}

interface SessionDetails {
  session: TestSession;
  messages: Message[];
  issues: Issue[];
}

export function ICPTestingAudit() {
  const [icps, setIcps] = useState<ICP[]>([]);
  const [selectedICP, setSelectedICP] = useState<string>('');
  const [sessions, setSessions] = useState<TestSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<SessionDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadICPs();
    loadAllSessions(); // Load all sessions initially
  }, []);

  useEffect(() => {
    // Load sessions when filters change
    loadAllSessions();
  }, [selectedICP, statusFilter]);

  const loadICPs = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/icp-testing/icps`);
      if (response.data.ok) {
        setIcps(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load ICPs:', error);
    }
  };

  const loadAllSessions = async () => {
    setLoading(true);
    try {
      const params: any = {};

      // Only include icp_filename if an ICP is selected
      if (selectedICP) {
        params.icp_filename = selectedICP;
      }

      // Only include status if not "all"
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await axios.get(`${API_BASE}/api/icp-testing/sessions`, { params });

      if (response.data.ok) {
        setSessions(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionDetails = async (sessionId: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/icp-testing/sessions/${sessionId}`);

      if (response.data.ok) {
        setSelectedSession(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load session details:', error);
      alert('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'in_progress':
        return '#2196F3';
      case 'stopped':
        return '#FF9800';
      case 'timeout':
        return '#f44336';
      case 'error':
        return '#9C27B0';
      default:
        return '#666';
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'in_progress':
        return '▶️';
      case 'stopped':
        return '⏹️';
      case 'timeout':
        return '⏱️';
      case 'error':
        return '❌';
      default:
        return '❓';
    }
  };

  const downloadConversation = (session: SessionDetails) => {
    const data = {
      session_info: session.session,
      conversation: session.messages,
      issues: session.issues
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test_conversation_${session.session.id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-main-content">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>📊 ICP Testing Audit & Review</h1>
        <button
          onClick={() => window.location.href = '/admin'}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ← Back to Admin
        </button>
      </div>

      <div className="admin-layout-with-sidebar">
        {/* Admin Navigation Sidebar */}
        <div className="admin-tabs-sidebar">
          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin'}
          >
            📱 WhatsApp Connection
          </button>

          {/* AI Agents Collapsible Section */}
          <div className="nav-section">
            <button
              className="tab-button section-header active-section"
              onClick={() => setExpandedSections(!expandedSections)}
            >
              <span className="section-title">🤖 AI Agents</span>
              <span className="expand-icon">{expandedSections ? "▼" : "▶"}</span>
            </button>

            {expandedSections && (
              <div className="nav-subsection">
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=ai-models'}
                >
                  ⚙️ AI Model Selection
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=create-agent'}
                >
                  ➕ Create Chat Agent
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=agents'}
                >
                  🤖 Agent Controls
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=system-prompt'}
                >
                  📝 System Prompt
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=knowledge'}
                >
                  📚 Knowledge Base
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=urls'}
                >
                  🌐 URL Management
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/admin?tab=rag'}
                >
                  🧩 RAG Management
                </button>
                <button
                  className="tab-button nested"
                  onClick={() => window.location.href = '/icp-testing'}
                >
                  🧪 ICP Testing
                </button>
                <button
                  className="tab-button nested active"
                  onClick={() => window.location.href = '/icp-testing/audit'}
                >
                  📊 ICP Testing Audit
                </button>
              </div>
            )}
          </div>

          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin?tab=tokens'}
          >
            Token Usage
          </button>
          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin?tab=llm-config'}
          >
            AI Models
          </button>
          <button
            className="tab-button"
            onClick={() => window.location.href = '/admin?tab=settings'}
          >
            ⚙️ System Settings
          </button>
        </div>

        {/* Right Side Content */}
        <div style={{ flex: 1, display: 'flex', gap: '20px' }}>
          {/* Filters Sidebar */}
          <div style={{
            width: '300px',
            backgroundColor: '#f5f5f5',
            padding: '20px',
            borderRadius: '8px',
            height: 'fit-content',
            flexShrink: 0
          }}>
            <h3 style={{ marginTop: 0 }}>Filters</h3>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Select ICP:
            </label>
            <select
              value={selectedICP}
              onChange={(e) => {
                setSelectedICP(e.target.value);
                setSelectedSession(null);
              }}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            >
              <option value="">-- Select ICP --</option>
              {icps.map(icp => (
                <option key={icp.filename} value={icp.filename}>
                  {icp.profile_name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Status:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="stopped">Stopped</option>
              <option value="timeout">Timeout</option>
              <option value="error">Error</option>
            </select>
          </div>

          {/* Summary Box - Always visible */}
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '4px',
            fontSize: '0.9em'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
              Summary:
            </div>
            <div>
              <strong>{sessions.length}</strong> test sessions found
            </div>
            {selectedICP && (
              <>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                  Filtered by ICP: {icps.find(icp => icp.filename === selectedICP)?.profile_name}
                </div>
                <div style={{ fontSize: '0.85em', color: '#666' }}>
                  {icps.find(icp => icp.filename === selectedICP)?.profile_type}
                </div>
              </>
            )}
            {statusFilter !== 'all' && (
              <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                Status: {statusFilter}
              </div>
            )}
            {!selectedICP && statusFilter === 'all' && (
              <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                Showing all recent test sessions
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '2em' }}>⏳</div>
              <div>Loading...</div>
            </div>
          ) : !selectedSession ? (
            /* Sessions List */
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '20px'
            }}>
              <h3 style={{ marginTop: 0 }}>
                Test Sessions ({sessions.length})
              </h3>

              {sessions.length === 0 ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  No test sessions found. Try adjusting the filters or select a different ICP.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Batch</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Date</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>ICP Profile</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Agent</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Status</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Turns</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Duration</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Booking</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((session, index) => (
                        <tr
                          key={session.id}
                          style={{
                            backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                          }}
                        >
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '0.9em', fontWeight: 'bold' }}>
                            {session.batch_number || '-'}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '0.9em' }}>
                            {formatDate(session.started_at)}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                            <div>
                              <div style={{ fontWeight: 'bold' }}>{session.icp_profile_name}</div>
                              <div style={{ fontSize: '0.85em', color: '#666' }}>{session.icp_profile_type}</div>
                            </div>
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
                            {session.agent_name} v{session.agent_version}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              backgroundColor: getStatusColor(session.status),
                              color: 'white',
                              fontSize: '0.85em',
                              fontWeight: 'bold'
                            }}>
                              {getStatusIcon(session.status)} {session.status}
                            </span>
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                            {session.total_turns}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                            {session.test_duration_seconds ? formatDuration(session.test_duration_seconds) : '-'}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                            {session.appointment_booked ? (
                              <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>✅ Yes</span>
                            ) : (
                              <span style={{ color: '#f44336' }}>❌ No</span>
                            )}
                          </td>
                          <td style={{ padding: '10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                            <button
                              onClick={() => loadSessionDetails(session.id)}
                              style={{
                                padding: '5px 15px',
                                backgroundColor: '#2196F3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.9em'
                              }}
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            /* Session Details View */
            <div>
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Conversation Details</h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => downloadConversation(selectedSession)}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      💾 Download JSON
                    </button>
                    <button
                      onClick={() => setSelectedSession(null)}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: '#607D8B',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ← Back to List
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '15px',
                  marginTop: '15px'
                }}>
                  <div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>Agent:</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedSession.session.agent_name} v{selectedSession.session.agent_version}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>Profile:</div>
                    <div style={{ fontWeight: 'bold' }}>{selectedSession.session.icp_profile_name}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>Status:</div>
                    <div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        backgroundColor: getStatusColor(selectedSession.session.status),
                        color: 'white',
                        fontSize: '0.85em',
                        fontWeight: 'bold'
                      }}>
                        {getStatusIcon(selectedSession.session.status)} {selectedSession.session.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>Total Turns:</div>
                    <div style={{ fontWeight: 'bold' }}>{selectedSession.session.total_turns}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>Duration:</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedSession.session.test_duration_seconds ? formatDuration(selectedSession.session.test_duration_seconds) : '-'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9em', color: '#666' }}>Appointment Booked:</div>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedSession.session.appointment_booked ? (
                        <span style={{ color: '#4CAF50' }}>✅ Yes</span>
                      ) : (
                        <span style={{ color: '#f44336' }}>❌ No</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Issues */}
              {selectedSession.issues.length > 0 && (
                <div style={{
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ff9800',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ marginTop: 0, color: '#ff9800' }}>
                    ⚠️ Issues Detected ({selectedSession.issues.length})
                  </h4>
                  {selectedSession.issues.map(issue => (
                    <div
                      key={issue.id}
                      style={{
                        backgroundColor: 'white',
                        padding: '10px',
                        borderRadius: '4px',
                        marginBottom: '10px'
                      }}
                    >
                      <div style={{ fontWeight: 'bold' }}>
                        Turn {issue.turn_number}: {issue.issue_type}
                      </div>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {issue.issue_description}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Conversation */}
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '20px'
              }}>
                <h4 style={{ marginTop: 0 }}>Conversation Transcript</h4>

                {selectedSession.messages.map((msg) => (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      marginBottom: '15px'
                    }}
                  >
                    <div style={{
                      maxWidth: '70%',
                      padding: '10px 15px',
                      borderRadius: '12px',
                      backgroundColor: msg.role === 'user' ? '#2196F3' : '#f5f5f5',
                      color: msg.role === 'user' ? 'white' : 'black'
                    }}>
                      <div style={{ fontSize: '0.8em', marginBottom: '5px', opacity: 0.8 }}>
                        {msg.role === 'user' ? '👤 User' : '🤖 Agent'} • Turn {msg.turn_number}
                        {msg.response_time_ms && ` • ${msg.response_time_ms}ms`}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                      <div style={{ fontSize: '0.75em', marginTop: '5px', opacity: 0.7 }}>
                        {formatDate(msg.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
